import { useEffect, useState } from 'react'

import { styled, css } from 'styled-components'

import { Typography, Colors, Breakpoints } from '@b-stock/fe-core/design-system'

import ArrowToggleButton from '@/buyer-portal/components/ArrowToggleButton'

const MainInfo = styled.div<{ $level: number; $isValueWarning?: boolean }>`
  &&& {
    ${({ $level }) =>
      $level === 1
        ? Typography.Title3
        : $level === 2
          ? Typography.Subtitle2
          : Typography.Subtitle3}
    display: flex;
    justify-content: space-between;
    color: ${({ $level }) =>
      $level === 1
        ? Colors.Semantic.Neutral.black
        : $level === 2
          ? Colors.Semantic.Neutral.very_dark_grey
          : Colors.Semantic.Neutral.dark_grey};
    font-size: ${({ $level }) =>
      $level === 1 ? '1.25rem' : $level === 2 ? '0.875rem' : '0.75rem'};
    span:last-child > span:last-child {
      color: ${({ $isValueWarning }) =>
        $isValueWarning
          ? Colors.Semantic.Warning.highlight
          : Colors.Semantic.BStock.onclick.lightBackground};
    }
  }
`

const TitleWrapper = styled.span`
  display: flex;
  gap: 0.5rem;
`

// flex layout is intentional for all rows: rows without a valueTag render a
// single child and are visually unaffected; rows with a valueTag need flex to
// align the tag and value side-by-side on the right
const ValueWrapper = styled.span<{ $level: number }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ $level }) => ($level === 1 ? '1.25rem' : '0.875rem')};
  ${({ $level }) =>
    $level === 3 &&
    css`
      color: ${Colors.Semantic.Neutral.very_dark_grey};
    `}
  ${({ $level }) =>
    $level === 3 &&
    css`
      font-weight: normal;
    `}
`

const SecondInfo = styled.div<{ $level: number }>`
  ${Typography.Body3}
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  font-weight: normal;
  ${({ $level }) =>
    $level === 3 &&
    css`
      ${Typography.Body4}
      margin-top: 0;
      color: ${Colors.Semantic.Neutral.dark_grey};
    `}
  ${({ $level }) =>
    $level === 1 &&
    css`
      padding-bottom: 0.75rem;
    `}
`

const Description = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  font-weight: normal;
`

const StyledUl = styled.ul<{ $level: number }>`
  font-weight: ${(o) => o.$level > 1 && 'normal'};
  list-style-type: none;
  margin-left: ${({ $level }) => ($level === 1 ? '0' : '1.5rem')};
  margin-top: ${({ $level }) => ($level === 3 ? '0.5rem' : '0')};
  margin-bottom: 0;
  padding: 0;
  list-style-type: none;

  ${({ $level }) =>
    $level > 1 &&
    css`
      font-weight: normal;
    `}
`

const StyledLi = styled.li<{ $level: number }>`
  ${({ $level }) =>
    $level === 1
      ? css`
          padding: 0.75rem 0;
          &:first-child {
            padding-top: 0;
          }
        `
      : $level === 2
        ? css`
            padding: 0.75rem 0;
            border-bottom: 0.0625rem solid
              ${Colors.Semantic.Neutral.medium_grey};
            &:last-child {
              border-bottom: none;
            }
          `
        : css`
            padding: 0.25rem 0;
          `}
  @media ${Breakpoints.max.medium} {
    padding-bottom: 0;
  }
`

export type CardOrderItem = {
  title: string
  tag?: React.ReactNode
  valueTag?: React.ReactNode
  value: number | React.ReactNode
  description?: string | React.ReactNode
  children?: CardOrderItem[]
  isValueWarning?: boolean
  /** Handed back to `onToggle` so callers identify a row without matching on
   * the localized `title`. */
  analyticsKey?: string
}

export const CardOrder = ({
  data,
  level = 1,
  autoCollapseOnDataChange = false,
  onToggle,
}: {
  data?: CardOrderItem[]
  level?: number
  autoCollapseOnDataChange?: boolean
  onToggle?: (analyticsKey: string | undefined, isExpanded: boolean) => void
}) => {
  const [isExpanded, setIsExpanded] = useState<{ [key: string]: boolean }>(
    () => {
      const initialState: { [key: string]: boolean } = {}
      data?.forEach((item) => {
        initialState[item.title] = false
      })
      return initialState
    }
  )

  useEffect(() => {
    if (autoCollapseOnDataChange) {
      const updatedState: { [key: string]: boolean } = {}
      data?.forEach((item) => {
        updatedState[item.title] = false
      })
      setIsExpanded(updatedState) // oxlint-disable-line react/set-state-in-effect
    }
  }, [data, autoCollapseOnDataChange])

  const toggleExpanded = (item: CardOrderItem) => {
    // Read before the update so `onToggle` gets the state being moved to.
    const nextExpanded = !isExpanded[item.title]
    setIsExpanded((isExpanded) => ({
      ...isExpanded,
      [item.title]: !isExpanded[item.title],
    }))
    onToggle?.(item.analyticsKey, nextExpanded)
  }

  // Nothing stops a breakdown carrying two lines that share a title (two
  // "Tax" rows, say), and React silently drops all but one of a set of
  // colliding keys. Titles stay the key, with an occurrence counter appended
  // for the duplicates only, so an unambiguous list keys exactly as before.
  const seenTitles = new Map<string, number>()
  const keyedData = data?.map((item) => {
    const occurrence = seenTitles.get(item.title) ?? 0
    seenTitles.set(item.title, occurrence + 1)
    return {
      item,
      key: occurrence === 0 ? item.title : `${item.title}#${occurrence}`,
    }
  })

  return (
    <StyledUl $level={level}>
      {keyedData?.length
        ? keyedData.map(({ item, key }) => (
            <StyledLi $level={level} key={key}>
              <MainInfo $level={level} $isValueWarning={item.isValueWarning}>
                <TitleWrapper>
                  <span>{item.title}</span>
                  {item.tag}
                </TitleWrapper>
                <ValueWrapper $level={level}>
                  {item.valueTag}
                  <span>{item.value}</span>
                </ValueWrapper>
              </MainInfo>
              {item.description || item.children?.length ? (
                <SecondInfo $level={level}>
                  <Description className="estimated-total-payment-description">
                    {item.description}
                  </Description>
                  {item.children?.length ? (
                    <ArrowToggleButton
                      expandedState={isExpanded[item.title] ?? false}
                      toggleExpandedState={() => toggleExpanded(item)}
                    />
                  ) : null}
                </SecondInfo>
              ) : null}
              {isExpanded[item.title] ? (
                <CardOrder
                  data={item.children}
                  level={level + 1}
                  autoCollapseOnDataChange={autoCollapseOnDataChange}
                />
              ) : null}
            </StyledLi>
          ))
        : null}
    </StyledUl>
  )
}
